import { StudentPreviewToolbarTab } from '@constants';
import { Alert, Modal, Tabs } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Last14DaysActivity } from './Last14DaysActivity';
import { StudentHistory } from './StudentHistory';
import { StudentInfo } from './StudentInfo';
import { HideUserSubscriptions } from './StudentInfo/HideUserSubscriptions';
import { StudentInternalNotes } from './StudentInternalNotes';
import { Wrapper } from './StudentPreview.styles';
import { StudentSpecificInfo } from './StudentSpecificInfo';
import { StudentTracking } from './StudentTracking';

import { useStudentPreview } from '@/hooks';
import { useProfile } from '@/store';
import { useStudentsDetail } from '@/store/students/useStudentsDetail';

type ITabs = keyof typeof StudentPreviewToolbarTab;

const MODAL_WIDTH = 900;

export const StudentPreview = () => {
  const { userId, setUserId } = useStudentPreview();
  const {
    roleCheck: { isTeacher }
  } = useProfile();
  const { isLoading, student } = useStudentsDetail(userId);
  const [activeTab, setActiveTab] = useState<ITabs>(
    isTeacher ? StudentPreviewToolbarTab.PROGRESS : StudentPreviewToolbarTab.INFO
  );
  const { t } = useTranslation();

  const handleCancel = () => setUserId();

  if (!userId || isLoading) return null;

  if (!student)
    return <Alert showIcon type="error" message={t('USER_PREVIEW_COULD_NOT_BE_LOADED')} />;

  const tabs = isTeacher
    ? [
        {
          label: t('STUDY_PROGRESS'),
          key: StudentPreviewToolbarTab.PROGRESS,
          children: <StudentTracking student={student} />
        },
        {
          label: t('INTERNAL_NOTES'),
          key: StudentPreviewToolbarTab.INTERNAL_NOTES,
          children: <StudentInternalNotes student={student} />
        }
      ]
    : [
        {
          label: t('GENERAL_INFORMATION'),
          key: StudentPreviewToolbarTab.INFO,
          children: <StudentInfo student={student} />
        },
        {
          label: t('STUDENT_SPECIFIC_INFORMATION'),
          key: StudentPreviewToolbarTab.SPECIFIC_INFO,
          children: <StudentSpecificInfo student={student} />
        },
        {
          label: t('STUDY_PROGRESS'),
          key: StudentPreviewToolbarTab.PROGRESS,
          children: <StudentTracking student={student} />
        },
        {
          label: t('LAST_14_DAYS'),
          key: StudentPreviewToolbarTab.LAST_14_DAYS,
          children: <Last14DaysActivity student={student} />
        },
        {
          label: t('HISTORY'),
          key: StudentPreviewToolbarTab.HISTORY,
          children: <StudentHistory student={student} />
        },
        {
          label: t('INTERNAL_NOTES'),
          key: StudentPreviewToolbarTab.INTERNAL_NOTES,
          children: <StudentInternalNotes student={student} />
        },
        {
          label: t('COURSES'),
          key: StudentPreviewToolbarTab.COURSES,
          children: <HideUserSubscriptions studentId={student._id} />
        }
      ];

  return (
    <Modal
      centered
      open={!!userId}
      closable
      onCancel={handleCancel}
      width={MODAL_WIDTH}
      footer={null}>
      <Wrapper>
        <Tabs activeKey={activeTab} onChange={key => setActiveTab(key as ITabs)} items={tabs} />
      </Wrapper>
    </Modal>
  );
};
